import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { apiClient, handleApiError } from '@/config/api';
import { CallPerformancePeriod, CallPerformanceResponse } from '@/types/api';

/**
 * "My Call Performance" — the mobile port of iflipweb2's Vaadin tab. Fetches the
 * combined score-card + recent-calls payload for the authenticated employee
 * (the backend scopes it from the JWT; the client only sends the period).
 *
 * ⚠️ Registered in BOTH store/index.ts and store/index.web.ts — a slice missing
 * from the web store is `undefined` on the PWA (blank-screen crash).
 */
interface CallPerformanceState {
  data: CallPerformanceResponse | null;
  period: CallPerformancePeriod;
  isLoading: boolean;
  error: string | null;
}

const initialState: CallPerformanceState = {
  data: null,
  period: 'THIS_WEEK',
  isLoading: false,
  error: null,
};

export const getCallPerformance = createAsyncThunk(
  'callPerformance/fetch',
  async (period: CallPerformancePeriod, { rejectWithValue }) => {
    try {
      return await apiClient.get<CallPerformanceResponse>(
        `/call-performance?period=${encodeURIComponent(period)}`
      );
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  }
);

const callPerformanceSlice = createSlice({
  name: 'callPerformance',
  initialState,
  reducers: {
    setPeriod: (state, action: { payload: CallPerformancePeriod }) => {
      state.period = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // The requested period becomes the selected period immediately (synchronously
      // on dispatch), so the LAST click always wins even during rapid toggling.
      .addCase(getCallPerformance.pending, (state, action) => {
        state.isLoading = true;
        state.error = null;
        state.period = action.meta.arg;
      })
      // Drop out-of-order responses: a slower earlier request can resolve after a
      // later one, so only apply a result that still matches the selected period.
      // Without this, toggling Last Week ⇄ 2 Weeks Ago flip-flops as stale
      // responses land.
      .addCase(getCallPerformance.fulfilled, (state, action) => {
        if (action.meta.arg !== state.period) {
          return;
        }
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(getCallPerformance.rejected, (state, action) => {
        if (action.meta.arg !== state.period) {
          return;
        }
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setPeriod, clearError } = callPerformanceSlice.actions;
export default callPerformanceSlice.reducer;
